/**
 * 後台 — 出貨(存摺直接出貨;寄存提領,不經訂單)
 *
 * 場景:會員瓶數存在燕窩存摺(跨多張訂單累積),要貨時說一聲 →
 * 客服輸入手機找到會員 → 看存摺各品類餘額 → 填要出的數量 + 物流 → 確認出貨。
 * 後端從存摺扣量(全有全無)、記 wallet_shipments、推 LINE 出貨通知給客人。
 *
 * Route: /admin/ship
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import AdminHeader from "../../components/AdminHeader";
import {
  adminCreateWalletShipment,
  adminListWalletShipments,
  listMembers,
  CARRIER_OPTIONS,
  CARRIER_LABELS,
  type WalletShipment,
} from "../../api/admin";
import { adminGetWallet, type WalletBalance } from "../../api/wallet";
import type { Member } from "../../api/members";
import { useAuthStore } from "../../store/authStore";
import { useUIStore } from "../../store/uiStore";
import { todayTpe } from "../../utils/date";


interface ShipRow {
  category: string;   // drink / fresh / giftbox / box / bag / legacy
  name: string;
  unit: string;
  balance: number;
}


export default function DirectShip() {
  const navigate = useNavigate();
  const role = useAuthStore((s) => s.role);
  const showToast = useUIStore((s) => s.showToast);

  // Step 1: 找會員(手機 / 姓名)
  const [member, setMember] = useState<Member | null>(null);
  const [memberQuery, setMemberQuery] = useState("");
  const [memberCandidates, setMemberCandidates] = useState<Member[]>([]);
  const [searching, setSearching] = useState(false);

  // Step 2: 存摺 + 出貨數量
  const [wallet, setWallet] = useState<WalletBalance | null>(null);
  const [loadingWallet, setLoadingWallet] = useState(false);
  const [qty, setQty] = useState<Record<string, number>>({});

  // Step 3: 物流
  const [carrier, setCarrier] = useState("");
  const [trackingNo, setTrackingNo] = useState("");
  const [shipDate, setShipDate] = useState(() => todayTpe());
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // 此會員最近出貨(存摺直接出貨)
  const [recent, setRecent] = useState<WalletShipment[]>([]);

  // 冪等 key — 同 OrderCreate 模式:失敗重試沿用同 key(後端不重扣),成功才換新
  const idempotencyKey = useRef<string | null>(null);

  useEffect(() => {
    if (role !== "admin") navigate("/admin/login", { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role]);

  // 會員搜尋 — debounce 400ms(手機 / 姓名 / Email)
  useEffect(() => {
    if (member) return;
    const q = memberQuery.trim();
    if (!q) {
      setMemberCandidates([]);
      return;
    }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await listMembers({ search: q, limit: 10 });
        setMemberCandidates(r.members);
      } catch {
        // ignore
      } finally {
        setSearching(false);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [memberQuery, member]);

  async function loadWallet(uid: string) {
    setLoadingWallet(true);
    try {
      const [w, s] = await Promise.all([
        adminGetWallet(uid),
        adminListWalletShipments(uid, 10),
      ]);
      setWallet(w);
      setRecent(s.shipments);
    } catch {
      showToast("存摺載入失敗", "error");
    } finally {
      setLoadingWallet(false);
    }
  }

  function pickMember(m: Member) {
    setMember(m);
    setMemberCandidates([]);
    setMemberQuery("");
    setQty({});
    setWallet(null);
    loadWallet(m.line_user_id);
  }

  function resetMember() {
    setMember(null);
    setWallet(null);
    setRecent([]);
    setQty({});
  }

  // 可出貨列:各品類餘額 > 0(含包材)+ 既有餘額(舊制)
  const rows: ShipRow[] = [];
  if (wallet) {
    for (const c of wallet.categories || []) {
      if ((c.balance || 0) > 0) {
        rows.push({ category: c.category, name: c.name, unit: c.unit, balance: c.balance });
      }
    }
    if ((wallet.balance_bottles || 0) > 0) {
      rows.push({ category: "legacy", name: "既有餘額", unit: "瓶", balance: wallet.balance_bottles });
    }
  }

  function setRowQty(category: string, v: number, max: number) {
    setQty((prev) => ({ ...prev, [category]: Math.max(0, Math.min(max, Math.round(v) || 0)) }));
  }

  const lines = rows
    .map((r) => ({ category: r.category, qty: qty[r.category] || 0 }))
    .filter((l) => l.qty > 0);
  const totalQty = lines.reduce((s, l) => s + l.qty, 0);

  async function submit() {
    if (!member) return;
    if (lines.length === 0) {
      showToast("請至少填一項出貨數量", "error");
      return;
    }
    if (!carrier) {
      showToast("請選擇物流方式", "error");
      return;
    }
    if (!idempotencyKey.current) {
      idempotencyKey.current = crypto.randomUUID();
    }

    const postOnce = () =>
      adminCreateWalletShipment({
        member_uid: member.line_user_id,
        lines,
        carrier,
        number: trackingNo.trim(),
        ship_date: shipDate,
        note: note.trim(),
        idempotency_key: idempotencyKey.current!,
      });

    const handleResult = (r: Awaited<ReturnType<typeof postOnce>>): boolean => {
      if (r.status === "ok") {
        showToast("出貨成功,已扣存摺並通知客戶", "success");
        return true;
      }
      if (r.status === "insufficient_balance") {
        const name = rows.find((x) => x.category === r.category)?.name || r.category;
        showToast(`「${name}」存摺餘額不足(剩 ${r.balance ?? "?"}),整筆未出貨`, "error");
      } else if (r.status === "invalid_ship_date") {
        showToast("出貨日期格式不正確", "error");
      } else if (r.status === "member_not_found") {
        showToast("找不到此會員", "error");
      } else {
        showToast(`出貨未成功:${r.status}`, "error");
      }
      return false;
    };

    setSubmitting(true);
    try {
      try {
        const ok = handleResult(await postOnce());
        if (ok) idempotencyKey.current = null;
        if (ok) afterSuccess();
      } catch {
        // 網路失敗自動重試一次 — 同 key,後端冪等不重扣
        const ok = handleResult(await postOnce());
        if (ok) idempotencyKey.current = null;
        if (ok) afterSuccess();
      }
    } catch {
      showToast("出貨可能已送出,請看下方最近出貨確認,切勿重複送出", "error");
      if (member) loadWallet(member.line_user_id);
    } finally {
      setSubmitting(false);
    }
  }

  function afterSuccess() {
    setQty({});
    setTrackingNo("");
    setNote("");
    if (member) loadWallet(member.line_user_id);
  }

  return (
    <div className="min-h-screen bg-cream pb-12">
      <AdminHeader active="ship" />

      <div className="px-6 mt-6 max-w-3xl space-y-4">
        {/* Step 1: 找會員 */}
        <div className="card">
          <div className="text-sm font-medium mb-3">1. 客戶編號(手機號碼)</div>
          {member ? (
            <div className="flex items-center justify-between bg-cream rounded-lg p-3">
              <div>
                <div className="font-medium">{member.name || member.display_name || "—"}</div>
                <div className="text-xs text-muted mt-0.5">
                  {member.phone || "—"} · {member.line_user_id.slice(0, 8)}…
                </div>
              </div>
              <button onClick={resetMember} className="text-xs text-coral hover:underline">
                重新選擇
              </button>
            </div>
          ) : (
            <>
              <input
                className="input"
                type="search"
                value={memberQuery}
                onChange={(e) => setMemberQuery(e.target.value)}
                placeholder="輸入手機號碼(或姓名)搜尋"
                autoFocus
              />
              {searching && <div className="text-xs text-muted mt-2">搜尋中…</div>}
              {memberCandidates.length > 0 && (
                <div className="mt-2 max-h-60 overflow-y-auto border border-line rounded-lg">
                  {memberCandidates.map((m) => (
                    <button
                      key={m.line_user_id}
                      onClick={() => pickMember(m)}
                      className="w-full text-left px-3 py-2 border-b border-line/50 last:border-0 hover:bg-cream/50"
                    >
                      <div className="text-sm">{m.name || m.display_name || "—"}</div>
                      <div className="text-xs text-muted">{m.phone || "—"} · {m.email || "—"}</div>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* Step 2: 存摺餘額 + 出貨數量 */}
        {member && (
          <div className="card">
            <div className="text-sm font-medium mb-3">2. 燕窩存摺 — 填本次出貨數量</div>
            {loadingWallet ? (
              <div className="text-sm text-muted py-4">存摺載入中…</div>
            ) : rows.length === 0 ? (
              <div className="text-sm text-muted py-4">此會員存摺目前沒有可出貨的餘額</div>
            ) : (
              <div className="space-y-2">
                {rows.map((r) => (
                  <div key={r.category} className="flex items-center gap-3 p-3 rounded-lg border border-line">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium">{r.name}</div>
                      <div className="text-xs text-muted">存摺餘額 {r.balance} {r.unit}</div>
                    </div>
                    <input
                      type="number"
                      min={0}
                      max={r.balance}
                      className="input w-24 text-right"
                      value={qty[r.category] ?? ""}
                      onChange={(e) => setRowQty(r.category, Number(e.target.value), r.balance)}
                      placeholder="0"
                    />
                    <span className="text-xs text-muted w-6">{r.unit}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Step 3: 物流 + 確認 */}
        {member && totalQty > 0 && (
          <>
            <div className="card space-y-3">
              <div className="text-sm font-medium">3. 物流資訊</div>
              <div>
                <label className="label">物流方式 *</label>
                <select className="input" value={carrier} onChange={(e) => setCarrier(e.target.value)}>
                  <option value="">請選擇</option>
                  {CARRIER_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label">追蹤單號(選填)</label>
                <input
                  className="input"
                  value={trackingNo}
                  onChange={(e) => setTrackingNo(e.target.value)}
                  placeholder="物流追蹤碼"
                />
              </div>
              <div>
                <label className="label">出貨日期 *</label>
                <input
                  className="input"
                  type="date"
                  value={shipDate}
                  onChange={(e) => setShipDate(e.target.value)}
                />
              </div>
              <div>
                <label className="label">備註(選填)</label>
                <input
                  className="input"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="例如:客人 LINE 要貨 / 自取"
                />
              </div>
            </div>

            <div className="card bg-primary/5 border-primary/20">
              <div className="text-sm font-medium mb-2">本次出貨</div>
              {lines.map((l) => {
                const r = rows.find((x) => x.category === l.category)!;
                return (
                  <div key={l.category} className="flex justify-between text-sm py-0.5">
                    <span>{r.name}</span>
                    <span className="font-medium">− {l.qty} {r.unit}</span>
                  </div>
                );
              })}
              <div className="text-xs text-muted mt-2">
                送出後自動扣「{member.name || "此會員"}」的燕窩存摺,並推出貨通知到客戶 LINE
              </div>
            </div>

            <button onClick={submit} disabled={submitting} className="btn-primary w-full">
              {submitting ? "出貨中…" : "確認出貨"}
            </button>
          </>
        )}

        {/* 此會員最近出貨紀錄 */}
        {member && recent.length > 0 && (
          <div className="card space-y-3">
            <div className="text-sm text-muted font-medium">最近出貨(共 {recent.length} 筆)</div>
            {recent.map((sh) => (
              <div key={sh.shipment_id} className="border border-line rounded-lg p-3 text-sm space-y-1">
                <div className="flex justify-between items-center">
                  <span className="text-xs text-muted">{sh.ship_date || "—"}</span>
                  <span className="text-xs text-muted">
                    {CARRIER_LABELS[sh.carrier] || sh.carrier || "未填物流"}
                    {sh.number ? ` · ${sh.number}` : ""}
                  </span>
                </div>
                <div className="text-xs space-y-0.5">
                  {(sh.lines || []).map((ln) => (
                    <div key={ln.category} className="flex justify-between">
                      <span>{ln.name}</span>
                      <span className="text-muted">− {ln.qty} {ln.unit}</span>
                    </div>
                  ))}
                </div>
                {sh.note && <div className="text-xxs text-muted">備註:{sh.note}</div>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
